import { useEffect, useState } from 'react';
import { GoChevronUp, GoChevronDown } from 'react-icons/go';

interface AccordionProps {
  children: JSX.Element;
  title: string;
  titleClassName?: string;
  /** Locks the section open — the header stops toggling. */
  isExpanded?: boolean;
  /** Starts open but still collapsible. Ignored when `isExpanded` is set. */
  defaultExpanded?: boolean;
  padding?: string;
  noFrame?: boolean;
  /**
   * Only the content scrolls when the accordion is height-limited (its parent
   * caps it, e.g. with a max-height): the title stays in view.
   */
  scrollContent?: boolean;
}

const Accordion: React.FC<AccordionProps> = ({
  children, title, isExpanded, defaultExpanded = false, titleClassName = 'text-lg font-medium mb-0 text-text-primary', noFrame, padding = 'p-3', scrollContent = false,
}) => {
  const [expanded, setExpanded] = useState(!!isExpanded || defaultExpanded);

  // Once forced open (e.g. to show an error inside), it stays open after the lock goes away.
  useEffect(() => {
    if (isExpanded) {
      setExpanded(true);
    }
  }, [isExpanded]);

  const handleClick = () => {
    if (isExpanded === true) {
      return;
    }
    setExpanded((e) => !e);
  };

  const showContent = isExpanded === true || expanded;
  const icon = showContent ? <GoChevronUp className='text-2xl' /> : <GoChevronDown className='text-2xl' />;

  return (
    <div className={` ${noFrame ? '' : 'glass-surface p-0'}${scrollContent ? ' flex flex-col min-h-0' : ''}`}>
      <div className={scrollContent ? 'flex flex-col min-h-0' : undefined}>
        <div className={`items-center cursor-pointer${scrollContent ? ' flex flex-col items-stretch min-h-0' : ''}`}>
          <div
            onClick={() => handleClick()}
            className={`flex justify-between items-center ${padding}${scrollContent ? ' shrink-0' : ''}`}
          >
            <span className={`${titleClassName}`}>{title}</span>
            {icon}
          </div>
          <div className={`${padding} pt-0${scrollContent ? ' min-h-0 overflow-y-auto' : ''}`} hidden={!showContent}>{children}</div>
        </div>
      </div>
    </div>
  );
};

export default Accordion;
